import type { MouseEvent } from "react";

import styles from "./Board.module.css";
import { Avatar } from "../../components/Avatar";
import { Chip } from "../../components/Chip";
import type { Task } from "../../data/types";
import { Icon } from "../../icons";
import { cx } from "../../lib/cx";
import { isTerminal } from "../../lib/status";
import { waitingOnAgentsLabel } from "../../lib/waitingOnAgents";

export type TaskCardProps = {
  readonly archived: boolean;
  // Registers the card's DOM node so the board can move focus during
  // keyboard navigation (roving tabindex).
  readonly cardRef?: (el: HTMLAnchorElement | null) => void;
  readonly href: string;
  readonly onArchive: () => void;
  readonly onArchiveTask: () => void;
  readonly onCardFocus?: () => void;
  readonly onOpen: () => void;
  readonly onToggleSel?: () => void;
  readonly selected: boolean;
  readonly showRepo?: string;
  // Roving tabindex: 0 for the one card that is the board's current keyboard
  // entry point, -1 for the rest. Defaults to -1 (mouse-only) when unset.
  readonly tabIndex?: number;
  readonly task: Task;
};

export const TaskCard = ({
  archived,
  cardRef,
  href,
  onArchive,
  onArchiveTask,
  onCardFocus,
  onOpen,
  onToggleSel,
  selected,
  showRepo,
  tabIndex,
  task,
}: TaskCardProps) => {
  const timeOnly = task.time.replace(/^\w+\s*·\s*/, "");
  const isFailed = task.status === "failed";
  // Archiving is "clear it off my board", which applies to a failure too.
  const isDone = isTerminal(task.status);
  const canCancel =
    task.status === "needs-input" ||
    task.status === "pending" ||
    task.status === "running";

  const stop = (e: MouseEvent, fn: () => void): void => {
    e.stopPropagation();
    fn();
  };

  const open = (e: MouseEvent<HTMLAnchorElement>): void => {
    const selectionClick = e.altKey && !e.metaKey && !e.ctrlKey && !e.shiftKey;
    if (selectionClick && onToggleSel) {
      e.preventDefault();
      onToggleSel();
      return;
    }
    if (
      e.button === 0 &&
      !e.altKey &&
      !e.metaKey &&
      !e.ctrlKey &&
      !e.shiftKey
    ) {
      e.preventDefault();
      onOpen();
    }
  };

  return (
    <div className={cx(styles.card, selected && styles.selected)}>
      <a
        aria-label={`Open ${task.title}`}
        className={styles.cardLink}
        data-task-card=""
        href={href}
        onClick={open}
        onFocus={onCardFocus}
        ref={cardRef}
        tabIndex={tabIndex ?? -1}
      />
      {onToggleSel ? (
        <button
          className={styles.check}
          onClick={(e) => stop(e, onToggleSel)}
          title="Select"
          type="button"
        >
          {selected ? <Icon.Check size={11} /> : null}
        </button>
      ) : null}
      <div className={styles.quick}>
        {canCancel ? (
          <button
            onClick={(e) => stop(e, onArchiveTask)}
            title="Archive agent — shuts it down first"
            type="button"
          >
            <Icon.Stop size={12} />
          </button>
        ) : null}
        <a href={href} onClick={open} title="Open">
          <Icon.External size={12} />
        </a>
      </div>
      <div className={styles.title}>{task.title}</div>
      {isFailed ? (
        <span className={styles.failed}>
          <Icon.X size={11} />
          Failed
        </span>
      ) : null}
      {task.waitingOnAgents ? (
        <span className={styles.waiting}>
          {waitingOnAgentsLabel(task.waitingOnAgents)}
        </span>
      ) : null}
      {task.provisioning ? (
        <span
          aria-label="Provisioning sandbox"
          className={cx("thinking", styles.provisioning)}
        >
          <span className="dot" />
          <span className="dot" />
          <span className="dot" />
          <span>Provisioning</span>
        </span>
      ) : null}
      <div className={styles.meta}>
        {showRepo ? (
          <Chip repo>
            <Icon.Repo size={10} />
            {showRepo}
          </Chip>
        ) : null}
        {task.branch ? (
          <Chip>
            <Icon.Branch size={10} />
            {task.branch.replace(/^tin\//, "")}
          </Chip>
        ) : null}
        {task.files > 0 && (
          <Chip>
            {task.files} files ·{" "}
            <span className={styles.statAdd}>+{task.add}</span>{" "}
            <span className={styles.statDel}>−{task.rem}</span>
          </Chip>
        )}
      </div>
      <div className={styles.foot}>
        <span className={styles.footId}>
          {task.displayId} · {timeOnly}
        </span>
        <div className={styles.footRight}>
          {/* Archiving is one-way — it tears the sandbox down — so an already
              archived card offers nothing here. */}
          {isDone && !archived ? (
            <button
              className={styles.archive}
              onClick={(e) => stop(e, onArchive)}
              title="Archive"
              type="button"
            >
              <Icon.Archive size={11} />
            </button>
          ) : null}
          <Avatar name={task.who} size={20} />
        </div>
      </div>
    </div>
  );
};
